import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuCircleOff, LuFlaskConical, LuFolderKanban, LuFolderX, LuReceipt, LuTrash2, LuX } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import ToggleChip from '../../components/data/ToggleChip';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { SelectInput } from '../../components/form/FormControls';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import AddTariffsModal from './AddTariffsModal';
import useTariffs, { DELETED_PROJECT_LABEL } from './useTariffs';
import useTariffText from './tariffText';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  TARIFF_COLUMNS,
  getFilterState,
} from './tariffColumns';
import TariffHeader from './parts/TariffHeader';
import TariffCell, { TariffCardRow, getCellTitle } from './parts/TariffCell';
import TariffDetailPane from './parts/TariffDetailPane';
import ProjectsWithoutTariffsModal from './parts/ProjectsWithoutTariffsModal';

const COLUMNS_STORAGE_KEY = 'tariffManagement.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Alt Yüklenici / Tarife Yönetimi (Tailwind), built like Çalışan / Müşteri
 * Yönetimi: head-count tiles, a table card that reaches at most the bottom of
 * the screen with header filters, a column menu and "go to page", and the
 * selected tariff's panel beside the table. The data logic lives in useTariffs.
 */
const SponsorManagementPage = () => {
  const tariffs = useTariffs();
  const {
    isMainAdmin, isLoading, isExporting, refreshNonce,
    allTariffs, filteredTariffs, totalTariffs, activeCount, inactiveCount, amountTotals, projectOptions, projectsWithoutTariffs,
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm, projectFilter, setProjectFilter, isFilterActive,
    showTestProjects, setShowTestProjects, showProjectsWithoutTariffs, setShowProjectsWithoutTariffs,
    selectedTariff, setSelectedTariff, handleEdit, handleDelete,
    showTariffModal, setShowTariffModal, tariffModalKey, setTariffModalKey,
    preselectedCustomer, setPreselectedCustomer, preselectedProject, setPreselectedProject,
    refreshTariffs, downloadExcel, downloadPdf,
  } = tariffs;
  const { language, locale, label, yesNo, typeLabel, formatDate, formatMoney } = useTariffText();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: TARIFF_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const hasLoaded = allTariffs.length > 0 || !isLoading;

  // Süzgeç, arama, test projeleri ya da yenileme değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([filters, searchTerm, projectFilter, showTestProjects, refreshNonce]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredTariffs, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // The selection follows refreshed data and is dropped once the tariff is gone
  // (never while the tariff form is open)
  useEffect(() => {
    if (!selectedTariff || showTariffModal) return;
    const current = allTariffs.find((tariff) => tariff.id === selectedTariff.id);
    if (!current) setSelectedTariff(null);
    else if (current !== selectedTariff) setSelectedTariff(current);
  }, [allTariffs, selectedTariff, showTariffModal, setSelectedTariff]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedTariff) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedTariff(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedTariff, showFilterSheet, setSelectedTariff]);

  const optionLabel = useCallback((key, value) => (key === 'isSubcontractor' ? yesNo(value) : value), [yesNo]);
  const columnLabel = useCallback((key) => label(`columns.${TARIFF_COLUMNS[key].id}`), [label]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: TARIFF_COLUMNS[key].type
      ? { type: TARIFF_COLUMNS[key].type, options: (TARIFF_COLUMNS[key].options || []).map((value) => ({ value, label: optionLabel(key, value) })) }
      : undefined,
  })), [columns.visibleKeys, columnLabel, optionLabel]);

  const filterChips = Object.keys(filters)
    .filter((key) => TARIFF_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => {
      const { type } = TARIFF_COLUMNS[key];
      let value = String(filters[key]);
      if (type === 'select') value = optionLabel(key, filters[key]);
      else if (type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) value = formatDate(value);
      return { key, label: columnLabel(key), value, state: getFilterState(key, filters[key]) };
    });

  const canClearFilters = filterChips.length > 0 || Boolean(searchTerm) || Boolean(projectFilter);

  // Eski "Yeni Tarife Ekle" düğmesi: seçim ve ön seçimler temizlenir, form yeniden kurulur
  const openNewTariff = () => {
    setSelectedTariff(null);
    setPreselectedCustomer(null);
    setPreselectedProject(null);
    setTariffModalKey((prev) => prev + 1);
    setShowTariffModal(true);
  };

  // Tarifesiz bir projeden: müşteri ve proje hazır seçili gelir
  const openTariffForProject = (project) => {
    setSelectedTariff(null);
    setPreselectedCustomer(project.customerId);
    setPreselectedProject(project.id);
    setTariffModalKey((prev) => prev + 1);
    setShowTariffModal(true);
  };

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedTariff ? filteredTariffs.findIndex((tariff) => tariff.id === selectedTariff.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredTariffs[selectedIndex + offset];
    if (!sibling) return;
    setSelectedTariff(sibling);
    reveal(sibling.id);
  };

  const shareOf = (count) => (totalTariffs > 0 ? Math.min(1, count / totalTariffs) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: totalTariffs, icon: LuReceipt, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
    { key: 'active', label: label('stats.active'), shortLabel: label('stats.activeShort'), value: activeCount, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(activeCount), shareTitle: shareTitle(activeCount), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500' },
    { key: 'inactive', label: label('stats.inactive'), shortLabel: label('stats.inactiveShort'), value: inactiveCount, icon: LuCircleOff, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', share: shareOf(inactiveCount), shareTitle: shareTitle(inactiveCount), barClass: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500' },
    { key: 'withoutTariffs', label: label('stats.withoutTariffs'), shortLabel: label('stats.withoutTariffsShort'), value: projectsWithoutTariffs.length, icon: LuFolderX, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', onClick: () => setShowProjectsWithoutTariffs(true), active: false },
  ];

  const paneProps = {
    tariff: selectedTariff,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredTariffs.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredTariffs.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedTariff(null),
    onEdit: () => handleEdit(selectedTariff),
    onDelete: () => setPendingDelete(selectedTariff),
  };

  const totalsText = Object.entries(amountTotals).map(([currency, total]) => formatMoney(total, currency === '-' ? '' : currency)).join(' • ');

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <TariffHeader
          withoutTariffsCount={projectsWithoutTariffs.length}
          onShowWithoutTariffs={() => setShowProjectsWithoutTariffs(true)}
          isExporting={isExporting}
          onExcel={downloadExcel}
          onPdf={downloadPdf}
          onAdd={openNewTariff}
        />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={(
                <>
                  <span className="tw-flex tw-items-center tw-gap-1">
                    <LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                    <SelectInput
                      aria-label={label('toolbar.project')}
                      value={projectFilter}
                      onChange={(e) => setProjectFilter(e.target.value)}
                      placeholder={label('toolbar.allProjects', { count: projectOptions.length })}
                      options={projectOptions.map((name) => ({ value: name, label: name === DELETED_PROJECT_LABEL ? label('table.deletedProject') : name }))}
                      className="tw-h-8 tw-min-w-[12rem] tw-rounded-full tw-text-xs"
                    />
                    {projectFilter && (
                      <button
                        type="button"
                        onClick={() => setProjectFilter('')}
                        aria-label={label('toolbar.clearProject')}
                        title={label('toolbar.clearProject')}
                        className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                      >
                        <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </button>
                    )}
                  </span>
                  {isMainAdmin && (
                    <ToggleChip
                      pressed={showTestProjects}
                      onChange={setShowTestProjects}
                      icon={LuFlaskConical}
                      label={label('toolbar.testProjects')}
                      title={showTestProjects ? label('toolbar.testProjectsHide') : label('toolbar.testProjectsShow')}
                      tone="violet"
                    />
                  )}
                  {totalsText && (
                    <span title={label('toolbar.totalsHint')} className="tw-ml-auto tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-truncate tw-rounded-full tw-bg-teal-500/10 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-teal-800 dark:tw-text-teal-200">
                      <span className="tw-font-medium tw-opacity-75">{label('toolbar.totals')}</span>
                      <span className="tw-truncate">{totalsText}</span>
                    </span>
                  )}
                </>
              )}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(tariff) => <TariffCardRow tariff={tariff} />}
                selectedId={selectedTariff?.id}
                onSelect={setSelectedTariff}
                isLoading={isLoading}
                isBusy={isLoading && allTariffs.length > 0}
                emptyLabel={label('table.empty')}
                canClearFilters={canClearFilters}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(tariff, key) => <TariffCell columnKey={key} tariff={tariff} />}
                getCellTitle={getCellTitle}
                selectedId={selectedTariff?.id}
                onSelect={setSelectedTariff}
                isLoading={isLoading}
                isBusy={isLoading && allTariffs.length > 0}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={canClearFilters}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredTariffs.length}
              rangeOf={label('table.rangeOf', { total: filteredTariffs.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: totalTariffs.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading && allTariffs.length > 0}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <TariffDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <TariffDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.filter((key) => TARIFF_COLUMNS[key].type).map((key) => ({
          key,
          label: columnLabel(key),
          type: TARIFF_COLUMNS[key].type,
          options: (TARIFF_COLUMNS[key].options || []).map((value) => ({ value, label: optionLabel(key, value) })),
        }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredTariffs.length}
      />

      <ProjectsWithoutTariffsModal
        isOpen={showProjectsWithoutTariffs}
        projects={projectsWithoutTariffs}
        onAddTariff={openTariffForProject}
        onClose={() => setShowProjectsWithoutTariffs(false)}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        tone="danger"
        icon={LuTrash2}
        title={label('delete.title')}
        message={label('delete.message', {
          type: typeLabel(pendingDelete?.type) || '',
          value: pendingDelete?.value || '',
          project: pendingDelete?.projectMissing ? label('table.deletedProject') : (pendingDelete?.projectName || '-'),
        })}
        confirmLabel={label('delete.confirm')}
        cancelLabel={label('common.cancel')}
        onConfirm={async () => {
          const target = pendingDelete;
          setPendingDelete(null);
          setSelectedTariff(null);
          await handleDelete(target.id);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      <AddTariffsModal
        key={tariffModalKey}
        show={showTariffModal}
        handleClose={async () => {
          setShowTariffModal(false);
          setPreselectedCustomer(null);
          setPreselectedProject(null);
          await refreshTariffs();
        }}
        selectedTariff={selectedTariff}
        preselectedCustomer={preselectedCustomer}
        preselectedProject={preselectedProject}
      />
    </>
  );
};

export default SponsorManagementPage;
